import './Header.scss'

import {LinkType, MainUrlTypes} from 'types'
import React, {RefObject, useState} from 'react'

import {CartIcon} from '../../webshop/layout/cart/CartIcon'
import {HeaderPopup} from './HeaderPopup'
import {Languages} from '../../layout/Languages'
import {Logo} from '../../ui/Logo'
import {Navlinks} from '../Navlinks'
import {SubnavigationType} from '../Subnavigation'
import {Theme} from '../../Theme'
import {style} from '../../../util/style'
import {useAutohide} from '../../../util/autohide'
import {useLanguage} from '../../../util/i18n'

const styles = style('header', [
	'logo',
	'desktopnav',
	'buttons',
	'mobile',
	'mobilenav'
])

export type HeaderType = {
	project_key: string
	main_pages: Array<MainUrlTypes>
	navigation: Array<LinkType>
	btn?: LinkType
	heroimg?: boolean
	innerHeaderRef: RefObject<HTMLDivElement>
}

export const Header: React.FC<HeaderType> = ({
	project_key,
	main_pages,
	navigation,
	btn,
	heroimg,
	innerHeaderRef
}) => {
	const isWebshop = project_key === 'webshop'
	const hasNavigation = navigation?.length > 0 || btn || isWebshop
	const language = useLanguage()
	const large = language === 'fr' || language === 'es'

	return (
		<header ref={innerHeaderRef} className={styles().mod({heroimg})}>
			<Theme.Container>
				<div className={styles('row')}>
					<div className={styles('row-left')}>
						<Logo className={styles.logo()} />
					</div>
					{hasNavigation && (
						<div className={styles('row-right').mod({large})}>
							<Navlinks
								styles={styles.desktopnav}
								links={navigation}
								mod={navigation.length > 2 ? 'large' : undefined}
							/>
							{!isWebshop && <HeaderButtons btn={btn} />}
							{isWebshop && (
								<CartIcon
									project_key={project_key}
									main_pages={main_pages}
									mod="header"
								/>
							)}
						</div>
					)}
				</div>
			</Theme.Container>
		</header>
	)
}

const HeaderButtons: React.FC<{
	btn?: LinkType
	className?: string
	onClickHandler?: () => void
}> = ({btn, className, onClickHandler = () => null}) => {
	if (!btn) return null

	return (
		<div className={styles.buttons().with(className)}>
			<Theme.Button to={btn.url} withIcon="arrow" onClick={onClickHandler}>
				{btn.description || btn.title}
			</Theme.Button>
		</div>
	)
}

export const HeaderMobile: React.FC<
	{
		project_key: string
		main_pages: Array<MainUrlTypes>
		navigation: Array<LinkType>
		btn?: LinkType
	} & SubnavigationType
> = ({
	project_key,
	main_pages,
	navigation,
	btn,
	subnavigation,
	translations
}) => {
	const [isOpen, setOpen] = useState(false)
	const {ref} = useAutohide(isOpen)
	const hasNavigation = navigation?.length > 0
	const hasSubNavigation = subnavigation?.length > 0
	const isWebshop = project_key === 'webshop'

	return (
		<header ref={ref} className={styles.mobile}>
			<HeaderPopup
				project_key={project_key}
				main_pages={main_pages}
				toggled={isOpen}
				toggle={setOpen}
			>
				<Theme.Container>
					<div className={styles.mobile('content')}>
						<Logo
							onClick={() => setOpen(false)}
							className={styles.mobile('content-logo')}
						/>
						{hasNavigation && (
							<Navlinks
								styles={styles.mobilenav}
								links={navigation}
								onClick={() => setOpen(false)}
							/>
						)}
						{!isWebshop && (
							<HeaderButtons
								btn={btn}
								className={styles.mobile('content-buttons')}
								onClickHandler={() => {
									setOpen(false)
								}}
							/>
						)}
						{hasSubNavigation && (
							<Navlinks
								styles={styles.mobilenav}
								mod="sub"
								onClick={() => setOpen(false)}
								links={subnavigation}
							/>
						)}
						{translations && (
							<Languages
								className={styles.mobile('content-languages')}
								translations={translations}
							/>
						)}
					</div>
				</Theme.Container>
			</HeaderPopup>
		</header>
	)
}
